/**
 * Execution detail page (/execution?id=…).
 *
 * Data comes from POST /executions/detail via the executionDetail slice, mapped
 * to the ExecutionDetailView view model. Renders the execution summary (headline
 * fields + the executionDetails document), then the payments carried by the
 * execution — found via SearchPayments (paymentIdentifiers=[executionId]) and
 * shown as analytics + a table. The landing-page search bar is kept at the top
 * (without the "Beacon Lookup" title) so another lookup can be run without
 * navigating back.
 */
import { useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import Box from '@cloudscape-design/components/box';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Alert from '@cloudscape-design/components/alert';
import Button from '@cloudscape-design/components/button';
import Spinner from '@cloudscape-design/components/spinner';
import Link from '@cloudscape-design/components/link';
import { useAppSelector, useAppDispatch } from 'src/store/hooks';
import {
  fetchExecutionDetail, clearExecutionDetail,
} from 'src/store/slices/executionDetail/executionDetailSlice';
import { searchPayments, clearSearchPayments } from 'src/store/slices/searchPayments/searchPaymentsSlice';
import { SearchTabs } from '../search/SearchTabs';
import { ExecutionSummary } from './summary/ExecutionSummary';
import { PaymentAnalytics } from '../payments/PaymentAnalytics';
import { PaymentDetailsTable } from '../payments/PaymentDetailsTable';

export const ExecutionDetailPage = () => {
  const [searchParams] = useSearchParams();
  const executionId = searchParams.get('id') ?? '';
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { data: detail, otherExecutionIds, loading, error, notFound } = useAppSelector(state => state.executionDetail);

  const load = () => {
    if (!executionId) return undefined;
    return dispatch(fetchExecutionDetail(executionId));
  };

  // Load whenever the id changes; abort any in-flight request.
  useEffect(() => {
    const promise = load();
    const paymentsPromise = executionId
      ? dispatch(searchPayments({ filters: { paymentIdentifiers: [executionId] }, pagination: { pageNumber: 1, pageSize: 100 } }))
      : undefined;
    return () => {
      promise?.abort();
      paymentsPromise?.abort();
      dispatch(clearExecutionDetail());
      dispatch(clearSearchPayments());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [executionId, dispatch]);

  const renderBody = () => {
    if (!executionId) {
      return (
        <Alert type="warning" header="No execution id">
          Provide an execution id, for example /execution?id=&lt;executionId&gt;.
        </Alert>
      );
    }
    if (loading) {
      return (
        <Box textAlign="center" padding="xxl">
          <Spinner size="large" />
          <Box variant="p" color="text-body-secondary">Loading execution {executionId}…</Box>
        </Box>
      );
    }
    if (error) {
      return (
        <Alert type="error" header="Failed to load execution" action={<Button onClick={load}>Retry</Button>}>
          {error}
        </Alert>
      );
    }
    if (notFound || !detail) {
      return (
        <Alert type="warning" header="Execution not found">
          No execution matched <strong>{executionId}</strong>. GetExecutionDetail matches by executionId;
          use execution search to resolve other identifiers.
        </Alert>
      );
    }
    return (
      <SpaceBetween size="l">
        {otherExecutionIds.length > 0 && (
          <Alert type="info" header={`${otherExecutionIds.length} other execution(s) matched this identifier`}>
            <SpaceBetween direction="horizontal" size="xs">
              {otherExecutionIds.map(otherId => {
                const href = `/execution?id=${encodeURIComponent(otherId)}`;
                return (
                  <Link key={otherId} href={href} onFollow={e => { e.preventDefault(); navigate(href); }}>
                    {otherId}
                  </Link>
                );
              })}
            </SpaceBetween>
          </Alert>
        )}

        <ExecutionSummary />
        <PaymentAnalytics />
        <PaymentDetailsTable />
      </SpaceBetween>
    );
  };

  return (
    <Box padding={{ horizontal: 'l', top: 'l', bottom: 'xl' }}>
      <SpaceBetween size="l">
        <SearchTabs />
        {renderBody()}
      </SpaceBetween>
    </Box>
  );
};
